import { useEffect, useMemo, useSyncExternalStore } from "react";
import type { MapDocument } from "../shared/types";
import { validateMapDocument } from "../shared/validation";

const STORAGE_PREFIX = "event-map:draft:v1:";
const RECOVERY_TTL = 24 * 60 * 60 * 1_000;
const GROUP_WINDOW = 750;
const RECOVERY_DELAY = 250;
const MB = 1_024 * 1_024;
type DraftStorage = Pick<
  Storage,
  "length" | "key" | "getItem" | "setItem" | "removeItem"
>;
export interface DraftRecovery {
  document: MapDocument;
  baseRevision: number;
  savedAt: number;
  /** The saved map has changed since this browser copy was made. */
  stale: boolean;
}
interface DraftHistorySnapshot {
  draft: MapDocument;
  canUndo: boolean;
  canRedo: boolean;
  recovery: DraftRecovery | null;
  recoveryWarning: string;
}
interface HistoryOptions {
  storage?: DraftStorage | null;
  now?: () => number;
  maxEntries?: number;
  maxHistoryBytes?: number;
  maxRecoveryBytes?: number;
  maxStorageBytes?: number;
}
interface ChangeGroup {
  target?: object | null;
  at?: number;
}
interface Entry {
  document: MapDocument;
  bytes: number;
}
interface StoredRecovery {
  version: 1;
  scope: string;
  mapId: string;
  baseRevision: number;
  savedAt: number;
  document: MapDocument;
}
const unavailableMessage =
  "Browser recovery is unavailable. Save your changes before leaving this page.";

/** State and persistence are separate from React so rapid calls share one latest draft. */
export function createDraftHistory(
  initialDocument: MapDocument,
  initialRevision: number,
  scope: string,
  options: HistoryOptions = {},
) {
  const now = options.now ?? Date.now;
  const maxEntries = options.maxEntries ?? 40;
  const maxHistoryBytes = options.maxHistoryBytes ?? 16 * MB;
  const maxRecoveryBytes = options.maxRecoveryBytes ?? 2 * MB;
  const maxStorageBytes = options.maxStorageBytes ?? 4 * MB;
  const storageKey = `${STORAGE_PREFIX}${encodeURIComponent(scope)}:${encodeURIComponent(initialDocument.id)}`;
  let storage: DraftStorage | null = null;
  let storageWarning = "";
  if (scope) {
    try {
      storage =
        options.storage === undefined
          ? typeof window !== "undefined"
            ? window.sessionStorage
            : null
          : options.storage;
    } catch {
      storageWarning = unavailableMessage;
    }
  }
  let baselineText = JSON.stringify(initialDocument);
  let currentText = baselineText;
  let baseRevision = initialRevision;
  let past: Entry[] = [];
  let future: Entry[] = [];
  let groupTarget: object | null = null;
  let groupAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();

  function validTimestamp(value: unknown): value is number {
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value <= now() + 60_000 &&
      now() - value <= RECOVERY_TTL
    );
  }
  function removeStored() {
    try {
      storage?.removeItem(storageKey);
    } catch {
      storageWarning = unavailableMessage;
    }
  }
  function loadRecovery(): DraftRecovery | null {
    if (!storage) return null;
    let raw: string | null;
    try {
      raw = storage.getItem(storageKey);
    } catch {
      storageWarning = unavailableMessage;
      return null;
    }
    if (!raw) return null;
    try {
      if (raw.length * 2 > maxRecoveryBytes) throw new Error("Too large");
      const value = JSON.parse(raw) as StoredRecovery;
      if (
        value.version !== 1 ||
        value.scope !== scope ||
        value.mapId !== initialDocument.id ||
        !Number.isSafeInteger(value.baseRevision) ||
        value.baseRevision < 0 ||
        !validTimestamp(value.savedAt)
      )
        throw new Error("Invalid recovery metadata");
      // Clearing a required title is a valid intermediate editing state. Keep
      // that unfinished text recoverable while still validating all geometry,
      // references and the rest of the document before it reaches the renderer.
      const unfinishedTitle =
        typeof value.document?.title === "string" &&
        value.document.title.length <= 160 &&
        !value.document.title.trim()
          ? value.document.title
          : undefined;
      const validated = validateMapDocument(
        unfinishedTitle === undefined
          ? value.document
          : { ...value.document, title: "Untitled map" },
      );
      const document =
        unfinishedTitle === undefined
          ? validated
          : { ...validated, title: unfinishedTitle };
      if (document.id !== initialDocument.id)
        throw new Error("Incorrect recovery map");
      if (JSON.stringify(document) === baselineText) {
        removeStored();
        return null;
      }
      return {
        document,
        baseRevision: value.baseRevision,
        savedAt: value.savedAt,
        stale: value.baseRevision !== initialRevision,
      };
    } catch {
      removeStored();
      return null;
    }
  }
  const recovery = loadRecovery();
  let snapshot: DraftHistorySnapshot = {
    draft: initialDocument,
    canUndo: false,
    canRedo: false,
    recovery,
    recoveryWarning: storageWarning,
  };
  function publish(next: Partial<DraftHistorySnapshot> = {}) {
    snapshot = {
      ...snapshot,
      ...next,
      canUndo: past.length > 0,
      canRedo: future.length > 0,
    };
    listeners.forEach((listener) => listener());
  }
  function endGroup() {
    groupTarget = null;
    groupAt = 0;
  }
  function trimHistory() {
    let bytes = [...past, ...future].reduce((sum, item) => sum + item.bytes, 0);
    while (
      past.length + future.length > maxEntries ||
      bytes > maxHistoryBytes
    ) {
      // The oldest undo is least useful. If only redo remains, discard its
      // furthest future state, retaining the next redo action when possible.
      const removed = past.length ? past.shift() : future.shift();
      if (!removed) break;
      bytes -= removed.bytes;
    }
  }
  function cancelTimer() {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }
  function storedEntries(): { key: string; bytes: number; savedAt: number }[] {
    if (!storage) return [];
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage!.key(index),
    ).filter((key): key is string => !!key?.startsWith(STORAGE_PREFIX));
    const entries: { key: string; bytes: number; savedAt: number }[] = [];
    for (const key of keys) {
      if (key === storageKey) continue;
      const raw = storage.getItem(key);
      if (!raw) continue;
      try {
        if (raw.length * 2 > maxRecoveryBytes) throw new Error("Too large");
        const value = JSON.parse(raw) as StoredRecovery;
        if (value.version !== 1 || !validTimestamp(value.savedAt))
          throw new Error("Expired");
        entries.push({ key, bytes: raw.length * 2, savedAt: value.savedAt });
      } catch {
        storage.removeItem(key);
      }
    }
    return entries.sort((a, b) => a.savedAt - b.savedAt);
  }
  function flushRecovery() {
    cancelTimer();
    // Do not overwrite a previous session copy while its recovery offer is
    // pending. The editor may still be used before making that choice.
    if (!storage || snapshot.recovery) return;
    if (currentText === baselineText) {
      removeStored();
      if (snapshot.recoveryWarning !== storageWarning)
        publish({ recoveryWarning: storageWarning });
      return;
    }
    const value: StoredRecovery = {
      version: 1,
      scope,
      mapId: initialDocument.id,
      baseRevision,
      savedAt: now(),
      document: snapshot.draft,
    };
    const raw = JSON.stringify(value);
    const bytes = raw.length * 2;
    if (bytes > maxRecoveryBytes || bytes > maxStorageBytes) {
      removeStored();
      publish({
        recoveryWarning:
          "This map is too large for browser recovery. Save your changes before leaving this page.",
      });
      return;
    }
    try {
      const entries = storedEntries();
      let total = entries.reduce((sum, item) => sum + item.bytes, bytes);
      for (const entry of entries) {
        if (total <= maxStorageBytes) break;
        storage.removeItem(entry.key);
        total -= entry.bytes;
      }
      storage.setItem(storageKey, raw);
      storageWarning = "";
      if (snapshot.recoveryWarning) publish({ recoveryWarning: "" });
    } catch {
      storageWarning = unavailableMessage;
      publish({ recoveryWarning: storageWarning });
    }
  }
  function queueRecovery() {
    cancelTimer();
    if (storage && !snapshot.recovery)
      timer = setTimeout(flushRecovery, RECOVERY_DELAY);
  }
  function change(next: MapDocument, group: ChangeGroup = {}) {
    if (next.id !== initialDocument.id)
      throw new Error("Draft history cannot switch maps.");
    const text = JSON.stringify(next);
    if (text === currentText) return;
    const at = group.at ?? now();
    const sameGroup =
      !!group.target &&
      group.target === groupTarget &&
      at >= groupAt &&
      at - groupAt <= GROUP_WINDOW &&
      !future.length;
    if (!sameGroup)
      past.push({ document: snapshot.draft, bytes: currentText.length * 2 });
    future = [];
    trimHistory();
    currentText = text;
    groupTarget = group.target ?? null;
    groupAt = at;
    publish({ draft: next });
    queueRecovery();
  }
  function undo() {
    const previous = past.pop();
    if (!previous) return;
    future.push({ document: snapshot.draft, bytes: currentText.length * 2 });
    currentText = JSON.stringify(previous.document);
    trimHistory();
    endGroup();
    publish({ draft: previous.document });
    queueRecovery();
  }
  function redo() {
    const next = future.pop();
    if (!next) return;
    past.push({ document: snapshot.draft, bytes: currentText.length * 2 });
    currentText = JSON.stringify(next.document);
    trimHistory();
    endGroup();
    publish({ draft: next.document });
    queueRecovery();
  }
  /** Saving moves the clean checkpoint without discarding local undo/redo. */
  function markSaved(document: MapDocument, revision: number) {
    if (document.id !== initialDocument.id)
      throw new Error("Draft history cannot switch maps.");
    cancelTimer();
    endGroup();
    baselineText = currentText = JSON.stringify(document);
    baseRevision = revision;
    storageWarning = "";
    removeStored();
    publish({
      draft: document,
      recovery: null,
      recoveryWarning: storageWarning,
    });
  }
  function reset(document: MapDocument, revision: number) {
    if (document.id !== initialDocument.id)
      throw new Error("Draft history cannot switch maps.");
    past = [];
    future = [];
    markSaved(document, revision);
  }
  function recover() {
    const candidate = snapshot.recovery;
    if (!candidate) return;
    endGroup();
    publish({ recovery: null });
    change(candidate.document);
    queueRecovery();
  }
  function discardRecovery() {
    removeStored();
    publish({ recovery: null, recoveryWarning: storageWarning });
    // Existing edits remain recoverable once the older candidate is dismissed.
    queueRecovery();
  }
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    change,
    markSaved,
    reset,
    undo,
    redo,
    recover,
    discardRecovery,
    flushRecovery,
    destroy() {
      flushRecovery();
      listeners.clear();
    },
  };
}

function activeEditTarget(): object | null {
  if (typeof document === "undefined") return null;
  const element = document.activeElement;
  if (!element) return null;
  if (element.tagName === "TEXTAREA") return element;
  if (element.tagName !== "INPUT") return null;
  const type = (element as HTMLInputElement).type;
  return ["text", "search", "number", "range", "url", "email", "tel"].includes(
    type,
  )
    ? element
    : null;
}

export function useDraftHistory(
  initialDocument: MapDocument,
  initialRevision: number,
  scope: string,
) {
  const history = useMemo(
    () => createDraftHistory(initialDocument, initialRevision, scope),
    // Saved revisions are adopted explicitly; parent rerenders
    // must not discard the current editor's undo stack or pending recovery.
    [initialDocument.id, scope],
  );
  const snapshot = useSyncExternalStore(
    history.subscribe,
    history.getSnapshot,
    history.getSnapshot,
  );
  useEffect(() => {
    const flush = () => history.flushRecovery();
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
      history.destroy();
    };
  }, [history]);
  return {
    ...snapshot,
    change: (next: MapDocument) =>
      history.change(next, { target: activeEditTarget() }),
    markSaved: history.markSaved,
    reset: history.reset,
    undo: history.undo,
    redo: history.redo,
    recover: history.recover,
    discardRecovery: history.discardRecovery,
  };
}
